import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, ArrowRight, Sparkles } from "lucide-react";
import AuthEditorial from "@/components/AuthEditorial";

export default function Login() {
  const { login, user, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      nav("/app");
    } catch (err_) {
      setErr(formatApiErrorDetail(err_.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.05fr_1fr]" style={{ background: "var(--bg-primary)" }}>
      <AuthEditorial
        kicker="Welcome back"
        headline={<>The signal is<br/><span className="italic" style={{ backgroundImage: "linear-gradient(90deg, #7C3AED 0%, #EC4899 38%, #F43F5E 68%, #F97316 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>still fresh.</span></>}
        subline="Three new stories, ready with talking points, whenever you sign in."
      />

      <div className="flex items-center justify-center p-8 md:p-14 relative">
        <form
          onSubmit={submit}
          className="w-full max-w-md fade-up"
          data-testid="login-form"
        >
          {/* Mobile-only brand mark */}
          <Link to="/" className="lg:hidden inline-flex items-center gap-2 mb-8 group" data-testid="login-brand-mobile">
            <BrandDots />
            <span className="font-display text-2xl tracking-tight font-semibold leading-none">Fount Stories</span>
          </Link>

          <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#7C3AED] font-semibold">
            Sign in
          </div>
          <h1 className="font-display text-5xl md:text-6xl tracking-[-0.03em] leading-[0.98] font-semibold mt-3">
            Welcome back.
          </h1>
          <p className="text-base text-gray-600 mt-4 leading-relaxed">
            Pick up where you left off — your niche, your feed, your voice.
          </p>

          <div className="mt-9 space-y-5">
            <FormField
              label="Email"
              testid="login-email-input"
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="you@studio.com"
              autoComplete="email"
            />
            <FormField
              label="Password"
              testid="login-password-input"
              type="password"
              value={password}
              onChange={setPassword}
              rightSlot={
                <Link
                  to="/forgot-password"
                  className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 hover:text-[#7C3AED] transition-colors"
                  data-testid="login-forgot-link"
                >
                  Forgot?
                </Link>
              }
              autoComplete="current-password"
            />

            {err && (
              <div
                data-testid="login-error"
                className="text-sm text-red-700 border border-red-200 bg-red-50 px-4 py-3 rounded-xl"
              >
                {err}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              disabled={busy}
              className="group w-full py-4 rounded-2xl font-semibold text-white text-[15px] tracking-tight bg-brand-gradient disabled:opacity-60 hover:opacity-95 transition-all inline-flex items-center justify-center gap-2 shadow-[0_18px_50px_-15px_rgba(124,58,237,0.55)]"
            >
              {busy ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Signing in
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                </>
              )}
            </button>

            <div className="pt-3 flex items-center gap-3">
              <div className="flex-1 h-px bg-[var(--border)]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-gray-400">or</span>
              <div className="flex-1 h-px bg-[var(--border)]" />
            </div>

            <p className="text-sm text-gray-600 text-center leading-relaxed">
              New to Fount?{" "}
              <Link
                to="/register"
                className="font-semibold text-black underline underline-offset-4 decoration-[#EC4899] decoration-2 hover:decoration-[#7C3AED] transition-colors"
                data-testid="login-to-register"
              >
                Create your account
              </Link>
            </p>
            <div className="flex items-center gap-1.5 justify-center font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400">
              <Sparkles size={11} strokeWidth={1.8} className="text-[#F97316]" />
              10 briefs on us · no card
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function FormField({ label, type = "text", value, onChange, placeholder, testid, rightSlot, autoComplete }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 font-semibold">
          {label}
        </label>
        {rightSlot}
      </div>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full mt-2 border border-[var(--border)] focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 outline-none px-4 py-3.5 text-[15px] bg-white rounded-xl transition-colors placeholder:text-gray-400"
      />
    </div>
  );
}

function BrandDots() {
  return (
    <span className="inline-flex items-end gap-[3px]" aria-hidden>
      <span style={{ display: "inline-block", width: 5, height: 18, background: "#7C3AED", borderRadius: 2 }} />
      <span style={{ display: "inline-block", width: 5, height: 14, background: "#EC4899", borderRadius: 2 }} />
      <span style={{ display: "inline-block", width: 5, height: 10, background: "#F97316", borderRadius: 2 }} />
    </span>
  );
}
